import React, { useEffect, useMemo, useState } from 'react';
import {
  X, Loader2, AlertTriangle, FileText, Calendar as CalIcon, Plus,
  Trash2, ChevronRight, Briefcase, Plane,
} from 'lucide-react';
import {
  getPersonAssignmentHistory,
  assignPersonAgreement,
  assignPersonAgreementBounded,
  deletePersonAssignment,
  listAgreements,
} from '../services/api';
import { useToast } from './Toast';
import { FEEDBACK } from '../services/feedbackMessages';
import { agreementsForCompany, isGlobalAgreement } from '../utils/agreements';

// Histórico de CONVENIOS (PersonAssignment) de un trabajador.
//
// Modos del formulario "Añadir asignación":
//   · "Desde ahora en adelante" (sin validTo)
//     → /api/personAssignment/assign — backend autocierra el activo anterior.
//   · "Periodo cerrado" (validFrom + validTo explícitos)
//     → /api/personAssignment/assign-bounded — backend resuelve solapamientos.
//
// Lo mantenemos como SOLO LECTURA + ALTA. La edición de un tramo existente
// requeriría endpoint PUT que el backend aún no expone; cuando esté, ampliar.
// Mientras tanto, para corregir un tramo el admin lo BORRA y crea uno nuevo.

const fmtDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso; }
};

const todayIso = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};

const AssignmentCard = ({ entry, isActive, onDelete, deleting }) => {
  const name = entry.assignAgreementName || `Convenio #${entry.agreementId}`;
  const vacationDays = entry.assignVacationDays;
  const holidayType = entry.assignHolidayType;
  return (
    <div className={`rounded-lg border p-3 ${isActive ? 'border-primary/40 bg-primary-soft/40' : 'border-slate-200 bg-white'}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="inline-flex items-center gap-1.5">
            <Briefcase size={13} className="text-primary shrink-0" />
            <span className="font-semibold text-ink text-sm truncate">{name}</span>
            {isActive && (
              <span className="text-[9px] font-bold text-white bg-primary px-1.5 py-0.5 rounded-full uppercase tracking-wider shrink-0">
                Vigente
              </span>
            )}
          </div>
          <div className="text-xs text-ink-muted mt-0.5 inline-flex items-center gap-1.5 flex-wrap">
            <CalIcon size={10} />
            <span className="tabular-nums">{fmtDate(entry.validFrom) || '—'}</span>
            <ChevronRight size={10} className="shrink-0" />
            <span className={`tabular-nums ${entry.validTo ? '' : 'italic text-ink-muted/60'}`}>
              {entry.validTo ? fmtDate(entry.validTo) : 'sin cerrar'}
            </span>
          </div>
        </div>
        <button
          onClick={() => onDelete(entry)}
          disabled={deleting}
          className="text-ink-muted hover:text-danger p-1 rounded-md hover:bg-danger/5 transition-colors disabled:opacity-40 shrink-0"
          title="Eliminar esta asignación (no se puede deshacer)"
        >
          {deleting ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
        </button>
      </div>

      {/* Solo mostramos atributos del CONVENIO en sí: vacaciones y cómputo de
          festivos. Las horas/mes ya no aplican aquí — el cómputo real sale
          de la jornada semanal del trabajador (sus workhours de lun a dom),
          no del campo monthlyHours del convenio. */}
      <div className="grid grid-cols-2 gap-1.5 mt-2.5 text-[11px]">
        <div className="rounded-md bg-slate-50 border border-slate-200/60 p-1.5">
          <div className="text-[9px] uppercase tracking-wider text-ink-muted font-semibold inline-flex items-center gap-1">
            <Plane size={9} /> Vacaciones
          </div>
          <div className="font-display font-bold text-ink tabular-nums">{vacationDays ?? '—'} d</div>
        </div>
        <div className="rounded-md bg-slate-50 border border-slate-200/60 p-1.5">
          <div className="text-[9px] uppercase tracking-wider text-ink-muted font-semibold">Cómputo</div>
          <div className="font-display font-bold text-ink capitalize text-[11px]">{holidayType || '—'}</div>
        </div>
      </div>
    </div>
  );
};

const WorkerAgreementHistoryModal = ({ open, worker, onClose, onChanged }) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [agreements, setAgreements] = useState([]);
  const [deletingId, setDeletingId] = useState(null);

  // Formulario de alta.
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState('bounded'); // 'open' | 'bounded'
  const [formAgreementId, setFormAgreementId] = useState('');
  const [formValidFrom, setFormValidFrom] = useState('');
  const [formValidTo, setFormValidTo] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const toast = useToast();

  const reload = async () => {
    if (!worker?.id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getPersonAssignmentHistory(worker.id);
      const arr = Array.isArray(data) ? data : [];
      // Ordenar descendente por validFrom para que arriba quede lo más reciente.
      arr.sort((a, b) => String(b.validFrom || '').localeCompare(String(a.validFrom || '')));
      setList(arr);
    } catch (err) {
      setError(err?.message || 'No se pudo cargar el histórico de convenios.');
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    reload();
    listAgreements().then((arr) => setAgreements(Array.isArray(arr) ? arr : [])).catch(() => setAgreements([]));
    // reset formulario al abrir
    setFormOpen(false);
    setFormMode('bounded');
    setFormAgreementId('');
    setFormValidFrom('');
    setFormValidTo('');
    setFormError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, worker?.id]);

  // Workaround del backend: GET /api/personAssignment/:id/history NO joinea
  // los datos del agreement (assignAgreementName, assignMonthlyHours,
  // assignVacationDays, assignHolidayType). La doc dice que deberían venir,
  // pero la implementación actual los omite. Hasta que se arregle el join
  // en backend, los rellenamos buscando el agreement por id en el catálogo
  // que ya tenemos cargado. No-op cuando el backend joinee correctamente.
  const enrichedList = useMemo(() => {
    return list.map((e) => {
      if (e.assignAgreementName) return e;
      const ag = agreements.find((a) => Number(a.id) === Number(e.agreementId));
      if (!ag) return e;
      return {
        ...e,
        assignAgreementName: ag.name,
        assignMonthlyHours: ag.monthlyHours,
        assignVacationDays: ag.vacationDays,
        assignHolidayType: ag.holidayType,
      };
    });
  }, [list, agreements]);

  // Selector de alta: solo convenios de la empresa del trabajador + globales.
  // `agreements` se queda completo para nombrar tramos históricos.
  const agreementOptions = useMemo(
    () => agreementsForCompany(agreements, worker?.companyId),
    [agreements, worker?.companyId]
  );

  const activeEntry = useMemo(() => enrichedList.find((e) => !e.validTo) || null, [enrichedList]);

  const handleDelete = async (entry) => {
    if (!window.confirm(`¿Eliminar la asignación de "${entry.assignAgreementName || 'convenio'}" del ${fmtDate(entry.validFrom)}?\n\nNo se puede deshacer.`)) return;
    setDeletingId(entry.id);
    try {
      await deletePersonAssignment(entry.id);
      toast.success(FEEDBACK.agreementAssignmentDeleted(entry.assignAgreementName));
      await reload();
      if (onChanged) onChanged();
    } catch (err) {
      // Toast y no `setError`: ese estado sustituye el listado entero por el
      // banner de error de carga.
      toast.error(err, 'No se pudo eliminar la asignación.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    if (!formAgreementId) { setFormError('Selecciona un convenio.'); return; }
    if (!formValidFrom) { setFormError('Indica la fecha de inicio.'); return; }
    if (formMode === 'bounded') {
      if (!formValidTo) { setFormError('En periodo cerrado debes indicar la fecha fin.'); return; }
      if (formValidTo < formValidFrom) { setFormError('La fecha fin no puede ser anterior al inicio.'); return; }
    }
    setSubmitting(true);
    try {
      const payload = {
        personId: worker.id,
        agreementId: Number(formAgreementId),
        validFrom: formValidFrom,
      };
      if (formMode === 'bounded') {
        await assignPersonAgreementBounded({ ...payload, validTo: formValidTo });
      } else {
        await assignPersonAgreement(payload);
      }
      toast.success(FEEDBACK.agreementAssigned({
        agreementName: agreements.find((a) => Number(a.id) === Number(formAgreementId))?.name,
        workerName: worker?.name,
        validFrom: formValidFrom,
        validTo: formMode === 'bounded' ? formValidTo : null,
      }));
      setFormOpen(false);
      setFormAgreementId('');
      setFormValidFrom('');
      setFormValidTo('');
      await reload();
      if (onChanged) onChanged();
    } catch (err) {
      setFormError(err?.message || 'No se pudo crear la asignación.');
      // Convenio de otra empresa (404 del backend nuevo): toast además del error en línea.
      if (err?.reason === 'agreement-not-in-company') toast.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 backdrop-blur-sm p-4">
      <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 shrink-0">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold inline-flex items-center gap-1.5">
              <FileText size={12} /> Histórico de convenios
            </div>
            <h2 className="font-display text-lg font-bold text-ink truncate">{worker?.name || 'Trabajador'}</h2>
          </div>
          <button
            onClick={onClose}
            className="text-ink-muted hover:text-ink p-1 rounded-lg hover:bg-slate-100 transition-colors"
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {/* Resumen vigente */}
          {!loading && activeEntry && (
            <div className="rounded-lg border border-primary/30 bg-primary-soft/40 p-3 flex items-start gap-2">
              <Briefcase size={14} className="text-primary shrink-0 mt-0.5" />
              <div className="text-xs text-ink">
                <span className="font-semibold">Convenio vigente:</span>{' '}
                <span className="font-bold">{activeEntry.assignAgreementName || `#${activeEntry.agreementId}`}</span>
                {' '}desde el {fmtDate(activeEntry.validFrom)}.
              </div>
            </div>
          )}

          {/* Toggle formulario */}
          <div className="flex items-center justify-between gap-2">
            <div className="text-xs text-ink-muted">
              {loading ? 'Cargando…' : list.length === 0 ? 'Sin asignaciones registradas.' : `${list.length} asignación${list.length === 1 ? '' : 'es'}`}
            </div>
            <button
              onClick={() => setFormOpen((v) => !v)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-primary hover:bg-primary-dark transition-colors"
            >
              <Plus size={12} /> {formOpen ? 'Cancelar' : 'Añadir asignación'}
            </button>
          </div>

          {/* Formulario de alta */}
          {formOpen && (
            <form onSubmit={handleSubmit} className="rounded-lg border-2 border-primary/30 bg-primary-soft/30 p-3 space-y-3">
              <div className="text-xs font-semibold text-ink">Nueva asignación de convenio</div>

              <div>
                <label className="text-[10px] uppercase tracking-wider text-ink-muted font-semibold block mb-1">Convenio</label>
                <select
                  value={formAgreementId}
                  onChange={(e) => setFormAgreementId(e.target.value)}
                  className="w-full text-sm px-2.5 py-1.5 rounded-md border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                  disabled={submitting}
                >
                  <option value="">— Selecciona —</option>
                  {agreementOptions.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name} ({a.vacationDays ?? '—'}d vacaciones){isGlobalAgreement(a) ? ' · global' : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-wider text-ink-muted font-semibold block mb-1">Tipo de asignación</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setFormMode('bounded')}
                    disabled={submitting}
                    className={`text-left text-xs rounded-md border-2 px-2.5 py-2 transition-colors ${
                      formMode === 'bounded'
                        ? 'border-primary bg-white text-ink'
                        : 'border-slate-200 bg-white/50 text-ink-muted hover:border-slate-300'
                    }`}
                  >
                    <div className="font-semibold">Periodo cerrado</div>
                    <div className="text-[10px] mt-0.5 leading-tight">Con fecha de fin. Útil para tramos históricos.</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormMode('open')}
                    disabled={submitting}
                    className={`text-left text-xs rounded-md border-2 px-2.5 py-2 transition-colors ${
                      formMode === 'open'
                        ? 'border-primary bg-white text-ink'
                        : 'border-slate-200 bg-white/50 text-ink-muted hover:border-slate-300'
                    }`}
                  >
                    <div className="font-semibold">Desde ahora en adelante</div>
                    <div className="text-[10px] mt-0.5 leading-tight">Sin fecha fin. Auto-cierra el vigente.</div>
                  </button>
                </div>
              </div>

              <div className={`grid gap-2 ${formMode === 'bounded' ? 'grid-cols-2' : 'grid-cols-1'}`}>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-ink-muted font-semibold block mb-1">Desde</label>
                  <input
                    type="date"
                    value={formValidFrom}
                    onChange={(e) => setFormValidFrom(e.target.value)}
                    max={formMode === 'open' ? todayIso() : undefined}
                    className="w-full text-sm px-2.5 py-1.5 rounded-md border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 tabular-nums"
                    disabled={submitting}
                  />
                </div>
                {formMode === 'bounded' && (
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-ink-muted font-semibold block mb-1">Hasta</label>
                    <input
                      type="date"
                      value={formValidTo}
                      onChange={(e) => setFormValidTo(e.target.value)}
                      min={formValidFrom || undefined}
                      className="w-full text-sm px-2.5 py-1.5 rounded-md border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 tabular-nums"
                      disabled={submitting}
                    />
                  </div>
                )}
              </div>

              {formError && (
                <div className="text-xs text-danger font-semibold inline-flex items-center gap-1.5">
                  <AlertTriangle size={12} /> {formError}
                </div>
              )}

              <div className="flex items-center gap-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-primary hover:bg-primary-dark transition-colors disabled:opacity-60"
                >
                  {submitting ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
                  Guardar asignación
                </button>
                <button
                  type="button"
                  onClick={() => setFormOpen(false)}
                  disabled={submitting}
                  className="text-xs text-ink-muted hover:text-ink px-2 py-1.5 rounded-md transition-colors disabled:opacity-60"
                >
                  Cancelar
                </button>
              </div>
            </form>
          )}

          {/* Estado de carga / error */}
          {loading && (
            <div className="text-center text-xs text-ink-muted py-6 inline-flex items-center gap-1.5 justify-center w-full">
              <Loader2 size={14} className="animate-spin" /> Cargando histórico…
            </div>
          )}
          {error && !loading && (
            <div className="rounded-lg border border-danger/30 bg-danger/5 p-3 flex items-start gap-2 text-xs">
              <AlertTriangle size={13} className="text-danger shrink-0 mt-0.5" />
              <div className="text-ink">{error}</div>
            </div>
          )}

          {/* Listado */}
          {!loading && !error && list.length === 0 && !formOpen && (
            <div className="text-center text-xs text-ink-muted py-6 italic">
              Este trabajador todavía no tiene asignaciones de convenio registradas.
              <br />
              <span className="text-[11px]">
                Si tu compi aún no ha lanzado la migración de Person.agreement, las asignaciones legacy no aparecerán aquí
                hasta que la ejecute.
              </span>
            </div>
          )}
          {!loading && enrichedList.length > 0 && (
            <div className="space-y-2">
              {enrichedList.map((e) => (
                <AssignmentCard
                  key={e.id}
                  entry={e}
                  isActive={!e.validTo}
                  onDelete={handleDelete}
                  deleting={deletingId === e.id}
                />
              ))}
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 shrink-0 text-[11px] text-ink-muted leading-relaxed">
          Para editar un tramo histórico el backend aún no expone PUT — borra y vuelve a crear.
        </div>
      </div>
    </div>
  );
};

export default WorkerAgreementHistoryModal;
